/**
 * User profile component - Terminal aesthetic
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { updateUserProfile } from '../services/authApi';
import { getMyAPIKeys, createMyAPIKey, revokeMyAPIKey, type APIKey } from '../services/api';
import './UserProfile.css';

interface UserProfileProps {
  onClose: () => void;
}

export function UserProfile({ onClose }: UserProfileProps) {
  const { user, refreshUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [isCreatingKey, setIsCreatingKey] = useState(false);
  const [freshKey, setFreshKey] = useState<string | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);

  useEffect(() => {
    loadAPIKeys();
  }, []);

  const loadAPIKeys = async () => {
    setKeysLoading(true);
    try {
      const keys = await getMyAPIKeys();
      setApiKeys(keys.filter(k => !k.revoked));
    } catch {
      // silently fail — API keys section is non-critical
    } finally {
      setKeysLoading(false);
    }
  };

  const handleCreateKey = async () => {
    setIsCreatingKey(true);
    try {
      const key = await createMyAPIKey(newKeyName.trim() || undefined);
      setFreshKey(key.key);
      setNewKeyName('');
      await loadAPIKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create API key');
    } finally {
      setIsCreatingKey(false);
    }
  };

  const handleRevokeKey = async (keyId: number) => {
    if (!confirm('Revoke this API key? This cannot be undone.')) return;
    try {
      await revokeMyAPIKey(keyId);
      await loadAPIKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke API key');
    }
  };

  const handleCopyFreshKey = async () => {
    if (!freshKey) return;
    await navigator.clipboard.writeText(freshKey);
    setKeyCopied(true);
    setTimeout(() => setKeyCopied(false), 2000);
  };

  if (!user) return null;

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setIsSaving(true);

    try {
      await updateUserProfile({
        display_name: displayName !== user.display_name ? displayName : undefined,
        avatar_url: avatarUrl !== user.avatar_url ? avatarUrl : undefined,
      });

      await refreshUser();

      setSuccess('Profile updated successfully');
      setIsEditing(false);

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setDisplayName(user.display_name);
    setAvatarUrl(user.avatar_url || '');
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  return (
    <div className="user-profile-modal" onClick={onClose}>
      <div className="user-profile-content" onClick={(e) => e.stopPropagation()}>
        <div className="profile-header">
          <h2>USER PROFILE</h2>
          <div className="profile-header-actions">
            {!isEditing && (
              <button className="edit-icon-btn" onClick={() => setIsEditing(true)} aria-label="Edit profile">
                ✎
              </button>
            )}
            <button className="close-btn" onClick={onClose} aria-label="Close">
              X
            </button>
          </div>
        </div>

        <div className="profile-body">
          {!isEditing ? (
            <div className="profile-info">
              <div className="info-row">
                <span className="info-label">USERNAME:</span>
                <span className="info-value">{user.username}</span>
              </div>
              <div className="info-row">
                <span className="info-label">DISPLAY NAME:</span>
                <span className="info-value">{user.display_name}</span>
              </div>
              <div className="info-row">
                <span className="info-label">EMAIL:</span>
                <span className="info-value">{user.email}</span>
              </div>
              <div className="info-row">
                <span className="info-label">ROLES:</span>
                <span className="info-value">{user.roles.map(r => r.toUpperCase()).join(', ')}</span>
              </div>
              <div className="info-row">
                <span className="info-label">MEMBER SINCE:</span>
                <span className="info-value">
                  {new Date(user.created_at).toLocaleDateString()}
                </span>
              </div>

              <div className="api-keys-section">
                <div className="api-keys-header">
                  <span className="info-label">API KEYS:</span>
                  {keysLoading && <span className="keys-loading">LOADING...</span>}
                </div>

                {freshKey && (
                  <div className="fresh-key-box">
                    <div className="fresh-key-label">NEW KEY — COPY NOW, WON'T BE SHOWN AGAIN:</div>
                    <textarea className="fresh-key-value" readOnly value={freshKey} rows={2} />
                    <div className="fresh-key-actions">
                      <button className="copy-key-btn" onClick={handleCopyFreshKey}>
                        {keyCopied ? 'COPIED!' : 'COPY'}
                      </button>
                      <button className="dismiss-key-btn" onClick={() => setFreshKey(null)}>
                        DISMISS
                      </button>
                    </div>
                  </div>
                )}

                {!keysLoading && apiKeys.length === 0 && !freshKey && (
                  <div className="no-keys">NO ACTIVE API KEYS</div>
                )}

                {apiKeys.map(key => (
                  <div key={key.id} className="api-key-row">
                    <div className="api-key-info">
                      <span className="api-key-name">{key.name || '(UNNAMED)'}</span>
                      <span className="api-key-date">
                        CREATED {new Date(key.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <button
                      className="revoke-key-btn"
                      onClick={() => handleRevokeKey(key.id)}
                    >
                      REVOKE
                    </button>
                  </div>
                ))}

                <div className="create-key-row">
                  <input
                    type="text"
                    className="key-name-input"
                    placeholder="KEY NAME (OPTIONAL)"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    disabled={isCreatingKey}
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateKey()}
                  />
                  <button
                    className="create-key-btn"
                    onClick={handleCreateKey}
                    disabled={isCreatingKey}
                  >
                    {isCreatingKey ? 'CREATING...' : '+ CREATE KEY'}
                  </button>
                </div>
              </div>

              {error && <div className="error-message">ERROR: {error}</div>}
            </div>
          ) : (
            <div className="profile-edit">
              <div className="form-group">
                <label htmlFor="edit-display-name">DISPLAY NAME:</label>
                <input
                  id="edit-display-name"
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  disabled={isSaving}
                />
                <small style={{ opacity: 0.7, fontSize: '0.85em' }}>
                  Username ({user.username}) cannot be changed
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="edit-avatar">AVATAR URL:</label>
                <input
                  id="edit-avatar"
                  type="text"
                  value={avatarUrl}
                  onChange={(e) => setAvatarUrl(e.target.value)}
                  placeholder="HTTPS://EXAMPLE.COM/AVATAR.JPG"
                  disabled={isSaving}
                />
              </div>

              {error && <div className="error-message">ERROR: {error}</div>}
              {success && <div className="success-message">SUCCESS: {success}</div>}

              <div className="edit-actions">
                <button
                  className="save-btn"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'SAVING...' : 'SAVE'}
                </button>
                <button
                  className="cancel-btn"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  CANCEL
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
